import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { ApiError } from '../../api/problem'
import { useCampaignList, useCreateCampaign } from '../../domains/campaigns/queries'
import { ArcaneCircle, Banner, Button, Card, Input } from '../../design/components'
import styles from './campaigns.module.css'

/**
 * The campaigns the caller runs or plays in.
 *
 * The City tab lands here rather than on a city, for the same reason the Sheet tab
 * lands on the character roster: a city belongs to one campaign, and the app has no
 * concept of a "current" one to guess at.
 */
export function CampaignListPage() {
  const [creating, setCreating] = useState(false)
  const query = useCampaignList()

  const campaigns = query.data?.items ?? []

  return (
    <AppShell title="Campaigns" subtitle="YOUR TABLES">
      {query.isPending && <Card>Loading…</Card>}

      {query.isError && (
        <Card>
          <Banner tone="error" urgent>
            Could not load your campaigns.
          </Banner>
        </Card>
      )}

      {query.isSuccess && campaigns.length === 0 && !creating && (
        <Card label="No campaigns yet">
          <p className={styles.note}>
            Start one to build a city, invite players and bring characters to the table.
          </p>
        </Card>
      )}

      {campaigns.length > 0 && (
        <ul className={styles.list}>
          {campaigns.map((campaign) => (
            <li key={campaign.id}>
              <Link to={`/campaigns/${campaign.id}`} className={styles.row}>
                <ArcaneCircle size={40} className={styles.sigil} />
                <span className={styles.rowText}>
                  <span className={styles.rowName}>{campaign.name}</span>
                  <span className={styles.rowMeta}>{summaryLine(campaign)}</span>
                </span>
                {campaign.isGameMaster === true && <span className={styles.gmBadge}>GM</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {creating ? (
        <CreateCampaignCard onDone={() => setCreating(false)} />
      ) : (
        <Button fullWidth onClick={() => setCreating(true)}>
          New campaign
        </Button>
      )}

      {query.isSuccess && query.data.totalCount > campaigns.length && (
        <p className={styles.note}>
          Showing {campaigns.length} of {query.data.totalCount}.
        </p>
      )}
    </AppShell>
  )
}

/** e.g. "THE CITY OF CHICAGO · 3 PLAYERS · GM MOLLY" */
function summaryLine(campaign: {
  cityName?: string | null
  playerCount?: number
  gameMasterName?: string | null
  isGameMaster?: boolean
}): string {
  const players = campaign.playerCount ?? 0
  const parts = [
    campaign.cityName ?? 'No city yet',
    players === 1 ? '1 player' : `${players} players`,
  ]

  // Naming the GM to the GM would be telling them who they are.
  if (campaign.isGameMaster !== true && campaign.gameMasterName != null) {
    parts.push(`GM ${campaign.gameMasterName}`)
  }

  return parts.join(' · ')
}

/**
 * Creating a campaign. Naming a city here creates it alongside the campaign; leaving
 * it blank starts world-less, and the city can be added later from the campaign page.
 * Both are legitimate, so the field is optional rather than required-with-a-default.
 */
function CreateCampaignCard({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate()
  const create = useCreateCampaign()

  const [name, setName] = useState('')
  const [cityName, setCityName] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    try {
      const sheet = await create.mutateAsync({
        name,
        ...(cityName.trim() === '' ? {} : { cityName: cityName.trim() }),
      })
      void navigate(`/campaigns/${sheet.campaignId}`)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not create the campaign.')
    }
  }

  return (
    <Card variant="fate" label="New campaign">
      <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
        {error !== null && (
          <Banner tone="error" urgent>
            {error}
          </Banner>
        )}

        <Input
          label="Name"
          name="name"
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Input
          label="City"
          name="cityName"
          value={cityName}
          placeholder="Chicago"
          hint="Optional — the campaign can gain a city later."
          onChange={(e) => setCityName(e.target.value)}
        />

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || name.trim() === ''}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Card>
  )
}
